import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, MessageSquare, RefreshCw, Send } from 'lucide-react';
import { API } from '../../App';
import { buildWhatsAppWebUrl } from '../../lib/whatsapp';
import { rankAlbums } from '../../lib/albumRecommendations';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { AlbumMultiSelect } from './AlbumMultiSelect';

const errorText = (error, fallback) => {
  const detail = error.response?.data?.detail;
  return typeof detail === 'string' ? detail : Array.isArray(detail) ? detail.map(item => item.msg).join('; ') : fallback;
};

export const InquiryMaterialsPrompt = ({ inquiry, apiReady, onComplete, initialShare = null }) => {
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState([]);
  const [phone, setPhone] = useState(inquiry.contactPhone || '');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState('');
  const [prepared, setPrepared] = useState(initialShare);
  const [opened, setOpened] = useState(false);
  const running = useRef(false);
  const endpoint = `${API}/crm/inquiry-materials/${inquiry.id}`;

  const loadFolders = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const { data } = await axios.get(`${API}/album/folders`);
      const albums = (data.folders || []).filter(f => f.name.trim().toLowerCase() !== 'brochures');
      setFolders(albums);
      setSelected(ids => ids.filter(id => albums.some(album => album.id === id)));
    } catch (e) {
      setError(errorText(e, 'Could not load albums. Check the Google Drive connection and retry.'));
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { loadFolders(); }, [loadFolders]);
  useEffect(() => { if (initialShare) setPrepared(initialShare); }, [initialShare]);

  const recordHandoff = async () => {
    await axios.post(`${endpoint}/handoff`, {}, { skipGlobalErrorToast: true });
    toast.success(`WhatsApp opened for ${inquiry.customerName} — tap Send in WhatsApp.`);
    onComplete();
  };

  const openPrepared = async (result, reservedWindow) => {
    const url = buildWhatsAppWebUrl(result.phone, result.message);
    const waWindow = reservedWindow && !reservedWindow.closed ? reservedWindow : window.open('', '_blank');
    if (!waWindow) {
      setError('Your browser blocked WhatsApp. Use Open WhatsApp below to continue.');
      return;
    }
    waWindow.opener = null;
    waWindow.location.href = url;
    setOpened(true);
    try { waWindow.focus(); } catch { /* Browser focus may be restricted. */ }
    await recordHandoff();
  };

  const share = async () => {
    if (running.current) return;
    if (!phone.trim()) { setError('Enter the customer’s WhatsApp number before sending.'); return; }
    if (!selected.length) { setError('Select at least one album.'); return; }
    running.current = true;
    // Reserve only on the explicit send click, never while selecting albums.
    const waWindow = !apiReady ? window.open('', '_blank') : null;
    if (waWindow) waWindow.opener = null;
    setBusy('send'); setError('');
    try {
      const { data } = await axios.post(`${endpoint}/share`, { folderIds: selected, phone }, { skipGlobalErrorToast: true });
      if (data.status === 'API_ACCEPTED' || data.status === 'WHATSAPP_OPENED') {
        if (waWindow) waWindow.close();
        toast.success(data.status === 'API_ACCEPTED' ? 'Albums and brochures accepted by WhatsApp.' : 'WhatsApp handoff already recorded.');
        onComplete();
      } else {
        setPrepared(data);
        await openPrepared(data, waWindow);
      }
    } catch (e) {
      // Never close a WhatsApp tab already navigated after a handoff bookkeeping error.
      try { if (waWindow?.location.href === 'about:blank') waWindow.close(); } catch { /* Cross-origin tab is already open. */ }
      setError(errorText(e, 'Sharing could not finish. Please retry; the inquiry is already saved.'));
    } finally { setBusy(''); running.current = false; }
  };

  const retryHandoff = async () => {
    if (running.current) return;
    running.current = true; setBusy('handoff'); setError('');
    try { if (opened) await recordHandoff(); else await openPrepared(prepared); }
    catch (e) { setError(errorText(e, 'Could not record the WhatsApp handoff. Please retry.')); }
    finally { setBusy(''); running.current = false; }
  };
  const visible = rankAlbums(folders, inquiry.productDescription).filter(f => f.name.toLowerCase().includes(search.trim().toLowerCase()));

  return (
    <Dialog open onOpenChange={() => {}}>
      <DialogContent showCloseButton={false} data-testid="required-inquiry-share-dialog"
        className="bg-white grid-cols-1 w-[calc(100%_-_2rem)] max-w-lg max-h-[90vh] overflow-y-auto"
        onEscapeKeyDown={e => e.preventDefault()} onInteractOutside={e => e.preventDefault()}>
        <DialogHeader className="min-w-0 text-left">
          <DialogTitle className="flex items-center gap-2"><MessageSquare className="w-5 h-5 text-green-600 shrink-0" /> Albums &amp; brochures</DialogTitle>
          <DialogDescription data-testid="required-share-customer" className="break-words [overflow-wrap:anywhere]">
            {inquiry.customerName} · {inquiry.inquiryCode}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 min-w-0">
          <p data-testid="required-share-mode" className="text-sm text-slate-600">
            {apiReady && !prepared ? 'Selected albums and brochures will be sent in one WhatsApp message.'
              : 'One prefilled message with your selected album links and brochures. Tap Send in WhatsApp.'}
          </p>
          {!prepared && <>
            <div><label htmlFor="required-share-phone" className="text-xs font-medium text-slate-600">Customer WhatsApp number</label>
              <Input id="required-share-phone" data-testid="required-share-phone" type="tel" autoComplete="tel" maxLength={40}
                value={phone} onChange={e => setPhone(e.target.value)} disabled={!!busy} placeholder="Mobile number" /></div>
            <div className="flex gap-2">
              <Input data-testid="required-share-search" aria-label="Search albums" placeholder="Search albums…"
                value={search} onChange={e => setSearch(e.target.value)} disabled={!!busy} />
              <Button data-testid="required-share-refresh" variant="outline" size="icon" className="shrink-0"
                title="Refresh albums" aria-label="Refresh albums" onClick={loadFolders} disabled={loading || !!busy}><RefreshCw className="w-4 h-4" /></Button>
            </div>
          </>}
          {error && <p role="alert" data-testid="required-share-error" className="text-sm text-red-700 break-words">{error}</p>}
          {prepared ? <div className="space-y-3">
            <p data-testid="required-share-message" className="text-sm whitespace-pre-wrap break-words text-slate-700">{prepared.message}</p>
            <Button data-testid="required-share-open-whatsapp" onClick={retryHandoff} disabled={!!busy} className="w-full bg-green-600 hover:bg-green-700">
              {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}{opened ? 'Record WhatsApp handoff' : 'Open WhatsApp'}
            </Button>
          </div> : loading ? <div data-testid="required-share-loading" role="status" className="py-6 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-amber-600" /><span className="sr-only">Loading albums</span></div> :
            <AlbumMultiSelect folders={folders} visible={visible} selected={selected} setSelected={setSelected} disabled={!!busy} />}
          {!prepared && <Button data-testid="required-share-send-selected" onClick={share}
            disabled={loading || !!busy || !phone.trim() || selected.length === 0} className="w-full bg-green-600 hover:bg-green-700">
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />} Send selected albums
          </Button>}
        </div>
      </DialogContent>
    </Dialog>
  );
};